import cx from 'classnames'
import _ from 'lodash'
import T from 'prop-types'
import React from 'react'
import ReactDOM from 'react-dom'
import {
  TypeaheadMenuOwner
} from 'react-base'
import {
  Editor,
  EditorState,
  ContentState,
  SelectionState,
  Modifier,
  KeyBindingUtil,
  getDefaultKeyBinding
} from 'draft-js'
import {
  NestableDecorator,
  getNewSelectionStateForRange,
  stripEntitiesFromContentBlock,
  replaceContentBlockText
} from './draftUtils'
import {
  IDE_PART_TYPES,
  stripSmartQuotes
} from './editorInputUtils'

const {hasCommandModifier} = KeyBindingUtil

const BLOCK_NESTING_DATA_KEY = 'nestedGroups'

// Draft IDE:
// 1: calls supplied mapper that converts raw text into a known IDE-state array
// 2: traverses the IDE-state and converts each part into a specified entity
// 3: supplies typeahead options

const IDE_PART_ENTITY_PROP_TYPES = {
  children: T.any,
  invalidMsg: T.string,
  entityKey: T.string.isRequired,
  offsetKey: T.string.isRequired,
  contentState: T.instanceOf(ContentState).isRequired
}

function ideKeyEntity(props) {
  const { className, tooltip, invalidMsg } = props.contentState
    .getEntity(props.entityKey)
    .getData()

  return (
    <span
      className={`ide_part ide_part_key ${className} ${invalidMsg
        ? 'is_invalid'
        : 'is_valid'}`}
      data-offset-key={props.offsetKey}
      data-tooltip={invalidMsg || tooltip}
    >
      {props.children}
    </span>
  )
}
ideKeyEntity.propTypes = IDE_PART_ENTITY_PROP_TYPES
ideKeyEntity.displayName = `DraftIDEPart.Key`

function ideValueEntity(props) {
  const { className, tooltip, invalidMsg } = props.contentState
    .getEntity(props.entityKey)
    .getData()

  return (
    <span
      className={`ide_part ide_part_value ${className} ${invalidMsg
        ? 'is_invalid'
        : 'is_valid'}`}
      data-offset-key={props.offsetKey}
      data-tooltip={invalidMsg || tooltip}
    >
      {props.children}
    </span>
  )
}
ideValueEntity.propTypes = IDE_PART_ENTITY_PROP_TYPES
ideValueEntity.displayName = `DraftIDEPart.Value`

function ideOperatorEntity(props) {
  const { className, tooltip, invalidMsg } = props.contentState
    .getEntity(props.entityKey)
    .getData()

  return (
    <span
      className={`ide_part ide_part_operator ${className} ${invalidMsg
        ? 'is_invalid'
        : 'is_valid'}`}
      data-offset-key={props.offsetKey}
      data-tooltip={invalidMsg || tooltip}
    >
      {props.children}
    </span>
  )
}
ideOperatorEntity.propTypes = IDE_PART_ENTITY_PROP_TYPES
ideOperatorEntity.displayName = `DraftIDEPart.Operator`

// Moved to simplified decorator range method below
// function ideGroupEntity (props) {
//   const {
//     className,
//     tooltip,
//     invalidMsg,
//     nestingDepth
//   } = props.contentState.getEntity(props.entityKey).getData()
//   const colorPct = Math.min(42 + Math.round(nestingDepth * 12), 255) // TODO make percentage of max depth?
//
//   return (
//     <span
//       className={ `ide_part ide_part_group depth_${ nestingDepth } ${ className } ${ !!invalidMsg ? 'is_invalid' : 'is_valid' }` }
//       data-offset-key={ props.offsetKey }
//       data-tooltip={ invalidMsg || tooltip }
//       style={ {
//         backgroundColor: `rgba(${ colorPct }, ${ colorPct }, ${ colorPct }, 1.0)`,
//         zIndex: nestingDepth * 10
//       } }
//     >{ props.children }</span>
//   )
// }

const IDE_PART_ENTITIES = {
  KEY: ideKeyEntity,
  VALUE: ideValueEntity,
  OPERATOR: ideOperatorEntity
}

const IDE_PART_ENTITY_NAMES = _.mapValues(
  IDE_PART_ENTITIES,
  (val, name) => name
)

// Decorator Components
function EditorGroupComponent(props) {
  return (
    <span
      className={`editor_nested_group ${props.className || ''}`}
      data-offset-key={props.offsetKey}
    >
      {props.children}
    </span>
  )
}
EditorGroupComponent.propTypes = {
  children: T.any,
  className: T.string,
  offsetKey: T.string.isRequired
}
EditorGroupComponent.displayName = `DraftIDEPart.Group`

/*
* Decorator Strategies
*/
function getEntityStrategyByType(entityType) {
  return function(contentBlock, callback, contentState) {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity()
      if (entityKey === null) {
        return false
      }
      return contentState.getEntity(entityKey).getType() === entityType
    }, callback)
  }
}

function groupNestingStrategy(contentBlock, callback) {
  const nestingData = contentBlock.getData().get(BLOCK_NESTING_DATA_KEY)
  if (!nestingData) {
    return
  }
  const groups = nestingData.toJS()
  const numGroups = groups.length
  for (var i = 0; i < numGroups; i++) {
    const group = groups[i]
    callback(group.start + 1, group.end - 1)
  }
}

function groupStartNestingStrategy(contentBlock, callback) {
  const nestingData = contentBlock.getData().get(BLOCK_NESTING_DATA_KEY)
  if (!nestingData) {
    return
  }
  const groups = nestingData.toJS()

  while (groups.length > 0) {
    const p = groups.pop()
    // console.log('start callback', p)
    callback(p.start, p.start + 1)
  }

  // const numGroups = groups.length

  // for (var i = 0; i < numGroups; i++) {
  //   const group = groups[i]
  //
  //   callback(group.start, group.start + 1)
  // }
}

function groupEndNestingStrategy(contentBlock, callback) {
  const nestingData = contentBlock.getData().get(BLOCK_NESTING_DATA_KEY)
  if (!nestingData) {
    return
  }
  const groups = nestingData.toJS()
  const numGroups = groups.length
  for (var i = 0; i < numGroups; i++) {
    const group = groups[i]
    callback(group.end - 1, group.end)
  }
}

// function numberStrategy(contentBlock, callback) {
//   findWithRegex(/\d+/g, contentBlock, callback)
// }

// function findWithRegex (regex, contentBlock, callback) {
//   const text = contentBlock.getText()
//   let matchArr, start
//   while ((matchArr = regex.exec(text)) !== null) {
//     start = matchArr.index
//     callback(start, start + matchArr[0].length)
//   }
// }
//
// const NumberSpan = (props) => {
//   return (
//     <span
//       className={ `test_number_decorator` }
//       data-offset-key={ props.offsetKey }
//     >{ props.children }</span>
//   )
// }

function addEntity(
  contentState,
  contentBlockKey,
  entityName,
  offset,
  length,
  data = {}
) {
  contentState = contentState.createEntity(entityName, 'MUTABLE', data)

  // Apply entity to idePart range
  return Modifier.applyEntity(
    contentState,
    getNewSelectionStateForRange(offset, length, contentBlockKey),
    contentState.getLastCreatedEntityKey()
  )
}

function mergeBlockDataWithKey(contentState, contentBlockKey, newBlockData) {
  const newBlockmap = contentState
    .getBlockMap()
    .update(contentBlockKey, block => {
      return block.merge({ data: block.getData().merge(newBlockData) })
    })
  return contentState.merge({
    blockMap: newBlockmap
  })
}

function applyIDEStateToContentState(
  contentState,
  contentBlockKey,
  ideState = [],
  selectionState
) {
  const ideStateLen = ideState.length
  const newGroupData = []

  for (let i = 0; i < ideStateLen; i++) {
    const idePart = ideState[i]

    if (idePart) {
      // // If the parser changed the text in the IDEState entry, modify the displayed text to match
      // const currentText = contentState.getBlockForKey(contentBlockKey).getText()
      // if (idePart.text.trim() !== currentText.trim()) {
      //   console.log(`~~~TEXT CHANGED (${idePart.text}) (${currentText})`)

      //   contentState = Modifier.replaceText(
      //     contentState,
      //     getNewSelectionStateForRange(idePart.offset, currentText.length, contentBlockKey),
      //     idePart.text
      //   )

      //   // Recalc selection state for new text
      //   const newCaretPosition = idePart.text.length
      //   selectionState = getNewSelectionStateForRange(newCaretPosition, newCaretPosition, contentBlockKey)
      // }

      // Apply any entity ranges specified in the IDE part
      switch (idePart.partType) {
        case IDE_PART_TYPES.VALUE:
          contentState = addEntity(
            contentState,
            contentBlockKey,
            idePart.entityName || IDE_PART_ENTITY_NAMES.VALUE,
            idePart.offset,
            idePart.offset + idePart.length,
            idePart
          )
          break
        case IDE_PART_TYPES.KEY_VALUE:
          // TODO add wrapper-like grouping ranges, if they prove necessarry
          //   // Add wrapper entity if provided
          //   {
          //   contentState,
          //   selectionState
          // } = addEntity(
          //     contentState,
          //     contentBlockKey,
          //     idePart.entityName,
          //     idePart.offset,
          //     idePart.offset + idePart.length,
          //     {}
          //   )

          if (idePart.children.key) {
            contentState = addEntity(
              contentState,
              contentBlockKey,
              idePart.keyEntityName || IDE_PART_ENTITY_NAMES.KEY,
              idePart.children.key.offset,
              idePart.children.key.offset + idePart.children.key.length,
              idePart.children.key
            )
          }
          if (idePart.children.operator) {
            // Add optional operator entity
            contentState = addEntity(
              contentState,
              contentBlockKey,
              idePart.operatorEntityName || IDE_PART_ENTITY_NAMES.OPERATOR,
              idePart.children.operator.offset,
              idePart.children.operator.offset +
                idePart.children.operator.length,
              idePart.children.operator
            )
          }
          if (idePart.children.value) {
            contentState = addEntity(
              contentState,
              contentBlockKey,
              idePart.valueEntityName || IDE_PART_ENTITY_NAMES.VALUE,
              idePart.children.value.offset,
              idePart.children.value.offset + idePart.children.value.length,
              idePart.children.value
            )
          }
          break
        case IDE_PART_TYPES.GROUP:
          newGroupData.push({
            start: idePart.offset,
            end: idePart.offset + idePart.length
          })
          break
        case IDE_PART_TYPES.INVALID_RANGE:
          contentState = addEntity(
            contentState,
            contentBlockKey,
            idePart.entityName || IDE_PART_ENTITY_NAMES.VALUE,
            idePart.offset,
            idePart.offset + idePart.length,
            idePart
          )
          break
        default:
          console.warn('No IDE_PART_TYPES match for idePart:', idePart)
      }
    }
  }

  // Update block's nesting data
  contentState = mergeBlockDataWithKey(
    contentState,
    contentBlockKey,
    _.set({}, BLOCK_NESTING_DATA_KEY, newGroupData)
  )

  return {
    contentState,
    selectionState
  }
}

function getContentStateFromParser(
  contentState,
  previousContentState,
  selectionState,
  parser,
  parserOptions,
  forceChange = false
) {
  // console.profile('getContentStateFromParser')
  // console.time('getContentStateFromParser')
  let outputTypeaheadState = {}

  const allContentBlocks = contentState.getBlockMap()
  // const allRawTexts = contentState.getPlainText(/\n/).split(/\n/)
  const blocks = contentState.getBlocksAsArray()
  const numBlocks = blocks.length
  const focusedBlockKey = selectionState.getFocusKey()

  for (let i = 0; i < numBlocks; i++) {
    const contentBlock = blocks[i]
    const contentBlockKey = contentBlock.getKey()
    const contentBlockData = contentBlock.getData()
    // const otherContentBlocks = allContentBlocks.delete(contentBlockKey)
    const allContentBlockTexts = allContentBlocks.reduce((out, otherBlock) => {
      out.push(otherBlock.getText())
      return out
    }, [])

    const prevBlock = previousContentState
      ? previousContentState.getBlockForKey(contentBlockKey)
      : null
    const isFocusedBlock = contentBlockKey === focusedBlockKey

    // If content changed, re-parse rule entities
    if (forceChange || !prevBlock || prevBlock !== contentBlock || isFocusedBlock) {
      contentState = stripEntitiesFromContentBlock(contentState, contentBlock)
      const rawText = stripSmartQuotes(contentBlock.getText())
      contentState = replaceContentBlockText(
        contentState,
        contentBlock,
        rawText
      )

      // Only provide a positive caretOffset for the focused contentBlock
      const blockCaretOffset = isFocusedBlock ? selectionState.getStartOffset() : -1


      // NOTE: parser must gracefully handle empty input text
      const {
        IDEState,
        IDEStateData,
        newText,
        typeaheadState
      } = parser(rawText, parserOptions, blockCaretOffset, allContentBlockTexts)

      if (typeaheadState) {
        outputTypeaheadState = {
          ...typeaheadState,
          focusedBlockKey
        }
      }

      // If the parser changed the text in the IDEState entry, modify the displayed text to match
      if (newText && newText !== rawText) {
        contentState = replaceContentBlockText(
          contentState,
          contentBlock,
          newText
        )

        // Re-calc selection state for new text (move caret to end of contentBlock text)
        const newCaretPosition = newText.length
        selectionState = getNewSelectionStateForRange(newCaretPosition, newCaretPosition, contentBlockKey)
      }

      if (
        forceChange ||
        !_.isEqual(contentBlockData.toObject(), IDEStateData)
      ) {
        contentState = Modifier.setBlockData(
          contentState,
          getNewSelectionStateForRange(0, 0, contentBlockKey),
          contentBlockData.merge(IDEStateData)
        )
      }
      const newStates = applyIDEStateToContentState(
        contentState,
        contentBlockKey,
        IDEState,
        selectionState
      )
      contentState = newStates.contentState
      selectionState = newStates.selectionState
    } else {
      // This rule block did not change
    }
  }

  // console.timeEnd('getContentStateFromParser')
  return {
    contentState,
    selectionState,
    typeaheadState: outputTypeaheadState
  }
}

export default class EditorInput extends React.Component {
  static displayName = 'EditorInput'

  static propTypes = {
    blockStyleFn: T.func,
    className: T.string,
    initialValue: T.string,
    onBlur: T.func,
    onChange: T.func,
    onChangeArgs: T.array,
    onFocus: T.func,
    onHandleReturn: T.func,
    parser: T.func.isRequired, // Parsing function that must accept a raw ContentBlock string and return {IdeState, Data}
    parserOptions: T.object,
    placeholder: T.string,
    preventReturn: T.bool,
    readOnly: T.bool,

    // For TypeaheadMenuOwner
    optionHeight: T.number,
    omitExactMatch: T.bool,
    stopClickPropagation: T.bool,
    alwaysSelectFirstOption: T.bool,
    autoTypeaheadQuotes: T.bool, // If true any typeahead values inserted that contain spaces will be wrapped in double-quotation marks
    preventAutoSelect: T.bool
  }

  static defaultProps = {
    className: '',
    initialValue: '',
    onChangeArgs: [],
    preventReturn: true,
    autoTypeaheadQuotes: true,

    // Passthrough for TypeaheadMenuOwner
    optionHeight: 29,
    omitExactMatch: false,
    stopClickPropagation: true, // For TypeaheadMenuOwner
    alwaysSelectFirstOption: true, // For TypeaheadMenuOwner
    preventAutoSelect: false // For TypeaheadMenuOwner
  }

  constructor(props) {
    super(props)
    this.state = {
      editorState: this._initialValueToContentState(props.initialValue || ''),
      preventLineWrap: true,

      hasFocus: false,

      // For TypeaheadMenuOwner integration
      typeaheadState: {},
      // typeaheadOptions: [], //TEMP_OPTIONS,
      isMenuOpen: false
      // filterString: '',
      // preventAutoSelect: true
    }
  }

  componentDidMount() {
    this.emitChange() // initial change
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!_.isEqual(nextProps.initialValue, this.props.initialValue)) {
      this.setState(
        {
          editorState: this._initialValueToContentState(
            nextProps.initialValue || ''
          )
        },
        this.emitChange
      )
    }
  }

  componentWillUnmount() {
    this._isUnmounting = true
  }

  _initialValueToContentState = (value = '') => {
    const { parser, parserOptions } = this.props
    const initialContentState = ContentState.createFromText(value)
    const initialSelectionState = SelectionState.createEmpty(
      initialContentState.getLastBlock().getKey()
    )

    const {
      contentState
      /*selectionState*/
    } = getContentStateFromParser(
      initialContentState,
      null,
      initialSelectionState,
      parser,
      parserOptions
    )

    const editorState = EditorState.createWithContent(
      contentState,
      this._getDecorator()
    )

    return EditorState.moveFocusToEnd(editorState)
  }

  setValue = newValue => {
    this.setState(
      {
        editorState: this._initialValueToContentState(newValue)
      },
      this.emitChange
    )
  }

  clear = () => {
    this.setValue('')
  }

  focus = () => {
    this._editor && this._editor.focus()
  }

  blur = () => {
    this._editor && this._editor.blur()
  }

  _getDecorator = () => {
    // Decorator order should be innermost -> outermost (e.g. Number -> Clause -> Group)
    return new NestableDecorator(
      _.map(IDE_PART_ENTITIES, (entityComponent, entityTypeKey) => {
        return {
          strategy: getEntityStrategyByType(entityTypeKey),
          component: entityComponent
        }
      }).concat([
        // {
        //   strategy: numberStrategy,
        //   component: NumberSpan
        // },
        {
          strategy: groupNestingStrategy,
          component: EditorGroupComponent // TODO pass down props.className?
        },
        {
          strategy: groupStartNestingStrategy,
          component: EditorGroupComponent,
          props: {
            className: 'group_start'
          }
        },
        {
          strategy: groupEndNestingStrategy,
          component: EditorGroupComponent,
          props: {
            className: 'group_end'
          }
        }
      ])
    )
  }

  onChange = (editorState, forceChange = false) => {
    const { parser, parserOptions } = this.props
    const previousContent = this.state.editorState.getCurrentContent()
    const previousSelection = this.state.editorState.getSelection()
    const currentContent = editorState.getCurrentContent()
    const currentSelection = editorState.getSelection()
    const contentDidChange = previousContent !== currentContent
    const selectionDidChange = previousSelection !== currentSelection
    let newTypeaheadState = {}

    if (forceChange || contentDidChange || selectionDidChange) {
      const {
        contentState,
        selectionState,
        typeaheadState
      } = getContentStateFromParser(
        currentContent,
        previousContent,
        currentSelection,
        parser,
        parserOptions,
        forceChange
      )

      // console.log('~~~EditorInput: typeaheadState', typeaheadState)

      if (typeaheadState) {
        newTypeaheadState = typeaheadState
      }

      editorState = EditorState.set(editorState, {
        currentContent: contentState
      })

      if (selectionState !== currentSelection) {
        editorState = EditorState.forceSelection(
          editorState,
          selectionState
        )
      }
    }

    const isMenuOpen = editorState.getSelection().getHasFocus()
      && newTypeaheadState.typeaheadOptions
      && newTypeaheadState.typeaheadOptions.length > 0

    // Update editorState, then update typeahead state based on the
    // complete version of the editorState
    this.setState({
      editorState,
      isMenuOpen,
      typeaheadState: newTypeaheadState
    }, () => {
      if (contentDidChange || forceChange) {
        this.emitChange()
      }
    })
  }

  getCurrentState = () => {
    const blockMap = this.state.editorState.getCurrentContent().getBlockMap()
    return {
      textBlocks: blockMap.map(block => block.getText()).toArray(),
      textBlockData: blockMap.map(block => block.getData().toJS()).toArray()
    }
  }

  emitChange = () => {
    const {
      onChange
      // onChangeArgs
    } = this.props
    if (onChange) {
      const { textBlocks, textBlockData } = this.getCurrentState()
      onChange(textBlocks, textBlockData) //, ...onChangeArgs)
    }
  }

  // Programatically trigger a change event, e.g. to force revalidation
  triggerChange = () => {
    const { editorState } = this.state
    // Produce a new instance of editorstate
    // const newEditorState = EditorState.moveSelectionToEnd(editorState)
    this.onChange(editorState, true)
  }

  handleBeforeInput = (/*chars*/) => {
    // Prevent input if this would result in multiple spaces

    // FIXME find a smarter way to do this...
    // const editorState = this.state.editorState
    // const currSelection = editorState.getSelection()
    // const contentBlock = editorState.getCurrentContent().getBlockForKey(currSelection.getFocusKey())
    // const endOffset = currSelection.getEndOffset()
    // const blockText = contentBlock.getText()
    // const lastChar = blockText.slice(endOffset - 1, endOffset)
    // const nextChar = endOffset === (blockText.length - 1) ? ' ' : blockText.slice(endOffset, endOffset + 1)
    //
    // if (chars === ' ' && (lastChar === ' ' || nextChar === ' ')) {
    //   return 'handled'
    // }

    return 'not-handled'
  }

  handlePastedText = (/*rawText, html*/) => {
    return 'not-handled'
  }

  getSelectionIfFocused = editorState => {
    const selection = editorState.getSelection()
    if (!selection.getHasFocus()) {
      return false
    }
    return selection
  }

  getContentBlockForSelection = editorState => {
    const selection = this.getSelectionIfFocused(editorState)
    if (!selection) {
      return false
    }
    const contentState = editorState.getCurrentContent()
    return {
      contentBlock: contentState.getBlockForKey(selection.getStartKey()),
      selection: selection
    }
  }

  // getEntityAtSelection(offset = -1) {
  //   const { editorState } = this.state
  //   const block = this.getContentBlockForSelection()
  //   return block ? block.getEntityAt(editorState.getSelection().getStartOffset() - offset) : false
  // },

  onApplyMenuOption = option => {
    const {autoTypeaheadQuotes} = this.props
    let {editorState} = this.state
    const {
      typeaheadRange,
      focusedBlockKey
    } = this.state.typeaheadState
    let contentState = editorState.getCurrentContent()
    const contentBlock = contentState.getBlockForKey(focusedBlockKey)

    let insertedText = option.name

    if (autoTypeaheadQuotes && insertedText.indexOf(" ") !== -1) {
      // Wrap with quotes if value contains spaces
      insertedText = `"${insertedText}"`
    }

    if (option.needsSpaceAfter) {
      // Determine if a space is needed after the inserted text
      const rawText = contentBlock.getText()
      const charAfterReplacedText = rawText.substr(typeaheadRange[1], 1)

      if (charAfterReplacedText !== ' ') {
        insertedText += ' '
      }
    }

    contentState = Modifier.replaceText(
      contentState,
      getNewSelectionStateForRange(
        typeaheadRange[0],
        typeaheadRange[1],
        focusedBlockKey
      ),
      insertedText
    )

    // Push this as an undoable change
    editorState = EditorState.push(
      editorState,
      contentState,
      'insert-characters'
    )

    // Set selection to end of inserted text
    const newCaretPosition = typeaheadRange[0] + insertedText.length
    // const newCaretPosition = typeaheadRange[0] + insertedText.length + (option.needsSpaceAfter ? 1 : 0) // TODO make this smarter?

    editorState = EditorState.forceSelection(
      editorState,
      getNewSelectionStateForRange(newCaretPosition, newCaretPosition, focusedBlockKey)
    )

    // Apply change
    this.onChange(editorState)
  }

  getSelectionBoundingClientRect = () => {
    const domSelection = window.getSelection()

    if (domSelection.rangeCount > 0) {
      let rangeRect = domSelection.getRangeAt(0).getBoundingClientRect()
      if (rangeRect.height === 0 && rangeRect.width === 0) {
        // Various fallback positions
        if (domSelection.focusNode && domSelection.focusNode.getBoundingClientRect) {
          rangeRect = domSelection.focusNode.getBoundingClientRect()
        }
        else if (domSelection.anchorNode && domSelection.anchorNode.getBoundingClientRect) {
          rangeRect = domSelection.anchorNode.getBoundingClientRect()
        }
        else {
          const editorInputDomNode = ReactDOM.findDOMNode(this)
          rangeRect = editorInputDomNode.getBoundingClientRect()
        }
      }

      return rangeRect
    }
  }

  getMenuPosition = menuRect => {
    const {isMenuOpen} = this.state
    if (!isMenuOpen) {
      return [-99999, -99999]
    }
    const {left, bottom, height} = this.getSelectionBoundingClientRect()
    const lineHeight = height // Selection Height
    let menuX = Math.round(left)
    let menuY = Math.round(bottom)

    // Constrain to window bounds
    if (menuY + menuRect.height > window.innerHeight) {
      menuY = menuY - menuRect.height - lineHeight
    }
    if (menuX + menuRect.width > window.innerWidth) {
      menuX = menuX - menuRect.width
    }

    return [menuX, menuY]
  }

  _selectCurrentOption = () => {
    return this._typeaheadMenuRef.selectOption()
  }

  moveCaret = (toEnd = false) => {
    const { editorState } = this.state
    const { contentBlock } = this.getContentBlockForSelection(editorState)
    const contentBlockKey = contentBlock.getKey()
    // Get new typeahead state from updated editorState

    const newSelectionOffset = toEnd ? contentBlock.getLength() : 0

    const newEditorState = EditorState.forceSelection(
      editorState,
      getNewSelectionStateForRange(newSelectionOffset, newSelectionOffset, contentBlockKey)
    )

    this.onChange(newEditorState)
  }

  onFocus = () => {
    this.setState({
      hasFocus: true
    })

    if (this.props.onFocus) {
      this.props.onFocus()
    }
  }

  onBlur = () => {
    this.closeMenu()
    this.setState({
      hasFocus: false
    })
    if (this.props.onBlur) {
      this.props.onBlur()
    }
  }

  onKeyDown = e => {
    const keyCode = e.keyCode
    const hasModifier = hasCommandModifier(e)
    if (keyCode === 39 && hasModifier) {
      return 'move-caret-right'
    }
    if (keyCode === 37 && hasModifier) {
      return 'move-caret-left'
    }
    if (keyCode === 38) {
      this._typeaheadMenuRef.moveSelection(true)
      if (this.state.isMenuOpen) {
        e.preventDefault() // Only prevent if a menu "captured" it
        return 'noop-arrow-up'
      }      
    }
    if (keyCode === 40) {
      this._typeaheadMenuRef.moveSelection(false)
      if (this.state.isMenuOpen) {
        e.preventDefault() // Only prevent if a menu "captured" it
        return 'noop-arrow-down'
      }
    }
    if (keyCode === 9) {
      this._selectCurrentOption() // Select typeahead option
      if (this.state.isMenuOpen) {
        e.preventDefault()
        e.stopPropagation()
        return 'noop-tab'
      }
    }

    return getDefaultKeyBinding(e) // Fallback to default
  }

  handleKeyCommand = command => {
    if (command.startsWith('noop-')) {
      return 'handled'
    }
    if (command === 'move-caret-right') {
      this.moveCaret(true) // Emulate native "end" key behavior
      return 'handled'
    }
    if (command === 'move-caret-left') {
      this.moveCaret(false) // Emulate native "end" key behavior
      return 'handled'
    }

    return 'not-handled'
  }

  // openMenu = () => {
  //   this.setState({ isMenuOpen: true })
  // }

  closeMenu = () => {
    this.setState({
      typeaheadState: {}
      // isMenuOpen: false
    })
  }

  onHandleReturn = () => {
    const optionSelected = this._selectCurrentOption() // Select typeahead option

    if (this.props.onHandleReturn) {
      this.props.onHandleReturn(optionSelected)
    }

    // Ignore the return/newline if a Typeahead Option was inserted
    return optionSelected || this.props.preventReturn ? 'handled' : 'not-handled' // Prevent newLines (new blocks)
  }

  _bindEditorRef = el => {
    this._editor = el
  }

  _bindTypeaheadMenuRef = el => {
    this._typeaheadMenuRef = el
  }

  render() {
    const { state, props } = this
    const {
      className,
      placeholder,
      blockStyleFn,
      readOnly,
      optionHeight,
      stopClickPropagation,
      omitExactMatch,
      alwaysSelectFirstOption,
      preventAutoSelect
    } = props
    const {
      preventLineWrap,
      editorState,
      caretPosition,
      hasFocus,
      isMenuOpen,
      typeaheadState={}
    } = state

    const classes = cx(`editor_input`, className, {
      no_wrap: preventLineWrap,
      has_focus: hasFocus
    })
    // blockRendererFn={ blockRendererFn }
    // blockRenderMap={ extendedBlockRenderMap }


    return (
      <TypeaheadMenuOwner
        className={classes}
        menuClassName={`editor_input_typeahead_menu`}
        optionHeight={optionHeight}
        omitExactMatch={omitExactMatch}
        stopClickPropagation={stopClickPropagation}
        alwaysSelectFirstOption={alwaysSelectFirstOption}
        filterString={typeaheadState.matchText}
        isMenuOpen={isMenuOpen}
        preventAutoSelect={preventAutoSelect || _.get(typeaheadState, `matchText.length`, 0) === 0}
        getMenuPosition={this.getMenuPosition}
        closeMenu={this.closeMenu}
        onApplyMenuOption={this.onApplyMenuOption}
        options={typeaheadState.typeaheadOptions || []}
        ref={this._bindTypeaheadMenuRef}
      >
        <Editor
          blockStyleFn={blockStyleFn}
          editorState={editorState}
          handleBeforeInput={this.handleBeforeInput}
          handlePastedText={this.handlePastedText}
          handleReturn={this.onHandleReturn}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          onChange={this.onChange}
          handleKeyCommand={this.handleKeyCommand}
          keyBindingFn={this.onKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
          ref={this._bindEditorRef}
          spellCheck={false}
          stripPastedStyles
        />
        <span
          className={`query_input_caret caret_${hasFocus
            ? 'visible'
            : 'hidden'}`}
          style={caretPosition}
        />
      </TypeaheadMenuOwner>
    )
  }
}
